@use "@styles/_variables/colors" as *;
@use "@styles/_variables/breakpoints" as *;
@use "@styles/_variables/mixins" as *;
@use "@styles/_variables/sizes" as *;
@use "@styles/_variables/typography" as *;

.header {
  align-items: center;
  display: flex;
  margin: $tab-double $tab;

  .header-logo-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    user-select: none;
    cursor: pointer;
    margin-right: $header-logo-spacing;

    .header-logo {
      border-radius: $header-logo-size;
      width: $header-logo-size;
      // Track the .outline-outward ring: on hover it slides outward by
      // its outer+inner border widths (4px). Grow the photo's radius by
      // the same 4px and pull the margins in to match, so the layout box
      // stays exactly $header-logo-size at every transition frame and
      // the photo's edge keeps hugging the ring.
      transition:
        width 0.4s,
        margin 0.4s;
    }

    &:hover .header-logo {
      width: calc(#{$header-logo-size} + 8px);
      margin: -4px;
    }
  }

  .header-title {
    // The masthead is chrome, not copy: it used to pick ControlFreak up from
    // the global body default, which now carries the reading face — so the
    // identity voice has to be declared here.
    font-family: $font-display;
    transition:
      color 0.3s ease-in-out,
      text-shadow 0.3s ease-in-out;
    font-size: $header-title-font;
    color: $main-lightest;
    text-shadow: $text-glow;
    display: flex;
    align-items: center;
    justify-content: space-between;
    animation:
      header-title-boot 0.55s linear,
      header-title-pulse 2.8s ease-in-out 0.55s infinite alternate;
    &:hover {
      color: #fff;
      text-shadow: $text-glow-strong;
      animation: none;
    }
  }

  .menu {
    align-items: center;
    display: flex;
    flex-direction: row;
    margin-left: auto;
  }

  @media all and (max-width: $mobile-large) {
    margin-top: $tab-double-mobile-large;
    margin-bottom: $tab-double-mobile-large;
    margin-right: 0;
    .header-logo-wrapper {
      margin-right: $header-logo-spacing-mobile-large;
      .header-logo {
        width: $header-logo-size-mobile-large;
      }
      &:hover .header-logo {
        width: calc(#{$header-logo-size-mobile-large} + 8px);
      }
    }

    .header-title {
      font-size: $header-title-font-mobile-large;
    }
  }

  @media all and (max-width: $mobile-small) {
    margin-top: $tab-double-mobile-small;
    margin-bottom: $tab-double-mobile-small;
    margin-right: 0;
    .header-logo-wrapper {
      margin-right: $header-logo-spacing-mobile-small;
      .header-logo {
        width: $header-logo-size-mobile-small;
      }
      &:hover .header-logo {
        width: calc(#{$header-logo-size-mobile-small} + 8px);
      }
    }
    .header-title {
      font-size: $header-title-font-mobile-small;
      margin-right: 2.5vw;
    }
  }
}

.outline-outward {
  display: inline-block;
  $outerBorderWidth: 1px;
  $innerBorderWidth: 3px;

  position: relative;

  @include hideTapHighlightColor();
  @include hardwareAccel();
  @include improveAntiAlias();

  &:before {
    content: "";
    position: absolute;
    border: $main-lightest-transp solid $outerBorderWidth;
    border-radius: 10rem;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transition-duration: 0.4s;
    transition-property: top right bottom left border-color;
    box-shadow: 0 0 3px $gold-semi-transparent, 0 0 12px $gold-semi-transparent;
  }

  &:hover:before {
    top: -($outerBorderWidth + $innerBorderWidth);
    right: -($outerBorderWidth + $innerBorderWidth);
    bottom: -($outerBorderWidth + $innerBorderWidth);
    left: -($outerBorderWidth + $innerBorderWidth);
    animation: blinker ease-in-out forwards 0.4s;
    border-color: $main-lightest;
    box-shadow: 0 0 6px $gold-semi-transparent, 0 0 24px $gold-semi-transparent;
  }

  @keyframes blinker {
    33% {
      border-color: $main-lightest;
    }
    66% {
      border-color: $main-lightest-transp;
    }
    100% {
      border-color: $main-lightest;
    }
  }
}

// Holographic boot-in for the site heading, then a gentle powered idle pulse;
// mirrors the tab frame title (TabContainer) but settles at this title's
// brighter $text-glow rest state.
@keyframes header-title-boot {
  0% {
    opacity: 0;
    text-shadow: $text-glow-strong;
  }
  15% {
    opacity: 1;
  }
  30% {
    opacity: 0.3;
  }
  45% {
    opacity: 0.85;
  }
  60% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
    text-shadow: $text-glow;
  }
}

@keyframes header-title-pulse {
  0% {
    text-shadow: $text-glow;
  }
  100% {
    text-shadow: $text-glow-faint;
  }
}
